import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { AlertCircle } from "lucide-react";

import { AuthPanel, AuthPending, AuthScreen, Button } from "@h2j-data-team/ui";

import { authClient } from "@/lib/auth-client";
import { acceptInvitation, getInvitation, rejectInvitation } from "@/lib/api/invitations";
import { BrandLogo } from "@/components/brand-logo";
import { PRODUCT_NAME } from "@repo/shared";

export const Route = createFileRoute("/accept-invitation")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      id: typeof search.id === "string" ? search.id : undefined,
      email: typeof search.email === "string" ? search.email : undefined,
    };
  },
  head: () => ({ meta: [{ title: `Accept invitation — ${PRODUCT_NAME}` }] }),
  component: AcceptInvitationPage,
});

type InvitationState =
  | { status: "loading" }
  | { status: "needs-auth" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      invitation: { id: string; email: string; organizationId: string; role: string };
    }
  | { status: "done" };

function AcceptInvitationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { id: invitationId, email: invitedEmail } = Route.useSearch();
  const { data: sessionData, isPending } = authClient.useSession();

  const [state, setState] = useState<InvitationState>({ status: "loading" });
  const [pending, setPending] = useState<"accept" | "decline" | null>(null);

  useEffect(() => {
    if (!invitationId) {
      setState({ status: "error", message: t("invitation.noId") });
      return;
    }

    if (isPending) return;

    if (!sessionData) {
      setState({ status: "needs-auth" });
      return;
    }

    let cancelled = false;

    getInvitation(invitationId)
      .then((res) => {
        if (cancelled) return;
        if (res.error || !res.data) {
          setState({
            status: "error",
            message: res.error?.message ?? t("invitation.notFound"),
          });
          return;
        }
        setState({ status: "ready", invitation: res.data });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: "error", message: t("invitation.loadFailed") });
      });

    return () => {
      cancelled = true;
    };
  }, [invitationId, sessionData, isPending, t]);

  const handleAccept = async () => {
    if (!invitationId) return;
    setPending("accept");
    try {
      const res = await acceptInvitation(invitationId);
      if (res.error) {
        setState({
          status: "error",
          message: res.error.message ?? t("invitation.acceptFailed"),
        });
        return;
      }
      setState({ status: "done" });
      navigate({ to: "/dashboard" });
    } finally {
      setPending(null);
    }
  };

  const handleReject = async () => {
    if (!invitationId) return;
    setPending("decline");
    try {
      const res = await rejectInvitation(invitationId);
      if (res.error) {
        setState({
          status: "error",
          message: res.error.message ?? t("invitation.declineFailed"),
        });
        return;
      }
      setState({ status: "done" });
      navigate({ to: "/dashboard" });
    } finally {
      setPending(null);
    }
  };

  const invitationRedirect = invitationId ? `/accept-invitation?id=${invitationId}` : undefined;

  // Each state is a new panel (key): the title takes the focus when the state changes.
  return (
    <AuthScreen logo={<BrandLogo className="h-8 w-auto" />}>
      {state.status === "loading" && <AuthPending label={t("invitation.loading")} />}

      {state.status === "error" && (
        <AuthPanel
          key="error"
          tone="danger"
          icon={<AlertCircle />}
          title={t("invitation.error.heading")}
          description={state.message}
          actions={
            <Button size="lg" variant="outline" asChild>
              <Link
                to="/login"
                search={{
                  mode: undefined,
                  token: undefined,
                  error: undefined,
                  redirect: undefined,
                  email: undefined,
                }}
              >
                {t("invitation.error.goToSignIn")}
              </Link>
            </Button>
          }
        />
      )}

      {state.status === "needs-auth" && (
        <AuthPanel
          key="needs-auth"
          title={t("invitation.needsAuth.heading")}
          description={t("invitation.needsAuth.description", { productName: PRODUCT_NAME })}
          actions={
            <>
              <Button size="lg" asChild>
                <Link
                  to="/login"
                  search={{
                    mode: undefined,
                    token: undefined,
                    error: undefined,
                    redirect: invitationRedirect,
                    email: invitedEmail,
                  }}
                >
                  {t("invitation.needsAuth.signIn")}
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/signup" search={{ redirect: invitationRedirect, email: invitedEmail }}>
                  {t("invitation.needsAuth.createAccount")}
                </Link>
              </Button>
            </>
          }
        />
      )}

      {state.status === "ready" && sessionData && (
        <AuthPanel
          key="ready"
          title={t("invitation.ready.heading")}
          description={
            <Trans
              i18nKey="invitation.ready.description"
              ns="auth"
              values={{ role: state.invitation.role }}
              components={{ em: <em className="font-medium not-italic" /> }}
            />
          }
          actions={
            <>
              <Button
                size="lg"
                onClick={handleAccept}
                loading={pending === "accept"}
                disabled={pending === "decline"}
              >
                {pending === "accept"
                  ? t("invitation.ready.accepting")
                  : t("invitation.ready.accept")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleReject}
                loading={pending === "decline"}
                disabled={pending === "accept"}
              >
                {pending === "decline"
                  ? t("invitation.ready.declining")
                  : t("invitation.ready.decline")}
              </Button>
            </>
          }
        />
      )}
    </AuthScreen>
  );
}
